import {
  OPTION_FOOTER_ONLY,
  OPTION_HEADER_AND_FOOTER,
  OPTION_HEADER_ONLY,
  OPTION_WITH_UNIQUE_FOOTER
} from '@opengov/stories-shared/constants';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';
import React from 'react';

import { ImageUploader } from '../../../common/components/ImageUploader';
import { ISettings, IStoriesSettingsMessages } from '../../../../types';
import { MAX_IMAGE_UPLOAD_SIZE } from '../../../constants';
import { styles } from './LogoUpload.styles';

const imageStyle = { maxWidth: '1000px' };

function logoPlacementToString(placementObj: Record<string, boolean>): string {
  return (
    (placementObj[OPTION_HEADER_AND_FOOTER] && OPTION_HEADER_AND_FOOTER) ||
    (placementObj[OPTION_HEADER_ONLY] && OPTION_HEADER_ONLY) ||
    (placementObj[OPTION_WITH_UNIQUE_FOOTER] && OPTION_WITH_UNIQUE_FOOTER) ||
    (placementObj[OPTION_FOOTER_ONLY] && OPTION_FOOTER_ONLY)
  );
}

function logoPlacementToObj(placementString: string) {
  return {
    footer_only: placementString === OPTION_FOOTER_ONLY,
    header_as_footer: placementString === OPTION_HEADER_AND_FOOTER,
    header_only: placementString === OPTION_HEADER_ONLY,
    unique_footer: placementString === OPTION_WITH_UNIQUE_FOOTER
  };
}

export type ILogoUploadProps = {
  entitySubdomain: string;
  settings: ISettings;
  messages: IStoriesSettingsMessages;
  updateSettingsProps(newSettingsProps: Partial<ISettings>): void;
};

export const LogoUpload = ({ entitySubdomain, settings, messages, updateSettingsProps }: ILogoUploadProps) => {
  const { placement, header: logoHeaderUrl, footer: logoFooterUrl } = settings.brand.logo;
  const selectedLogoPlacement = logoPlacementToString(placement);

  const handleDrop = (_files: File[], _logoPosition: string) => {};

  const handleReset = (isFooterLogo: boolean) => {
    const logoPosition = isFooterLogo ? 'footer' : 'header';
    updateSettingsProps({
      brand: {
        ...settings.brand,
        logo: { ...settings.brand.logo, [logoPosition]: '' }
      }
    });
  };

  const handleUploadSuccess = (resultUrl: string, logoPosition: string) => {
    updateSettingsProps({
      brand: {
        ...settings.brand,
        logo: { ...settings.brand.logo, [logoPosition]: resultUrl }
      }
    });
  };

  const renderDropzone = (isFooterLogo = false) => {
    const logoPosition = isFooterLogo ? 'footer' : 'header';
    const url = (!isFooterLogo && logoHeaderUrl) || (isFooterLogo && logoFooterUrl);

    return (
      <Box sx={styles.dropzoneContainer}>
        <Typography variant="body2" sx={styles.dropzoneLabel}>
          {isFooterLogo ? messages.footerDropzoneLabel : messages.dropzoneLabel}
        </Typography>
        <ImageUploader
          entitySubdomain={entitySubdomain}
          style={imageStyle}
          url={url}
          onDrop={(files) => handleDrop(files, logoPosition)}
          onUploadSuccess={(resultUrl) => handleUploadSuccess(resultUrl, logoPosition)}
          onRemove={() => handleReset(isFooterLogo)}
        />
        <Typography variant="caption">{`${messages.maximumFileSize} ${MAX_IMAGE_UPLOAD_SIZE} MB`}</Typography>
      </Box>
    );
  };

  const optionsArray = [
    { optionType: OPTION_HEADER_AND_FOOTER, label: messages.logoOptionBoth },
    { optionType: OPTION_HEADER_ONLY, label: messages.logoOptionHeaderOnly },
    { optionType: OPTION_WITH_UNIQUE_FOOTER, label: messages.logoOptionUniqueFooter },
    { optionType: OPTION_FOOTER_ONLY, label: messages.logoOptionFooterOnly }
  ];

  return (
    <Box>
      <Box sx={styles.headerContainer}>
        <Typography variant="h5" sx={styles.heading}>{messages.logo}</Typography>
        <Typography variant="body2">{messages.uploadLogoText}</Typography>
      </Box>

      {renderDropzone(selectedLogoPlacement === OPTION_FOOTER_ONLY)}

      <Box sx={styles.radioGroup}>
        <RadioGroup
          aria-label={messages.logo}
          value={selectedLogoPlacement}
          onChange={(_e, value) => {
            updateSettingsProps({
              brand: {
                ...settings.brand,
                logo: { ...settings.brand.logo, placement: logoPlacementToObj(value) }
              }
            });
          }}
        >
          {optionsArray.map(({ optionType, label }) => (
            <FormControlLabel
              key={optionType}
              value={optionType}
              data-test="logo-placement"
              sx={styles.radioButton}
              control={
                <Radio
                  inputProps={{ className: 'no-format' } as React.InputHTMLAttributes<HTMLInputElement>}
                  sx={styles.radioInput}
                />
              }
              label={label}
            />
          ))}
        </RadioGroup>
      </Box>

      {selectedLogoPlacement === OPTION_WITH_UNIQUE_FOOTER && renderDropzone(true)}
    </Box>
  );
};
